// The cart builder's "Renew a domain" section: renew a domain the account
// already holds, for a number of years the live catalog sells.
//
// Owned domains come from the account's products (listAccountProducts →
// squish-backend lib/domain-product-adapter.js). The years come from
// /api/products/live `billingTerms.domain.termsMonths` (squish-backend
// lib/billing-terms.js), so this side never offers a term checkout refuses.
// The line is priced by squish-backend (src/lib/cartQuote.js): renewals price
// from the registrar's renewal rate, not the registration price.

// A domain still transferring in is not ours to renew yet; one whose
// auto-renew is off (CANCEL_SCHEDULED) is still held until its term ends, and
// renewing it is exactly how a rep keeps it.
export const RENEWABLE_STATES = new Set(['ACTIVE', 'CANCEL_SCHEDULED']);

// The account's renewable domains, sorted, each once.
export function renewableDomains(products) {
  const out = new Set();
  for (const p of Array.isArray(products) ? products : []) {
    if (p?.productKind !== 'domain' || !RENEWABLE_STATES.has(p.state)) continue;
    const domain = String(p.productId || '').trim().toLowerCase();
    if (domain) out.add(domain);
  }
  return [...out].sort();
}

const yearsLabel = (years) => `${years} year${years === 1 ? '' : 's'}`;

// [{ termMonths, years, label }] the catalog sells for domains, shortest first.
export function renewalTerms(billingTerms) {
  const months = billingTerms?.domain?.termsMonths;
  if (!Array.isArray(months)) return [];
  return months
    .filter((m) => Number.isInteger(m) && m > 0 && m % 12 === 0)
    .sort((a, b) => a - b)
    .map((termMonths) => ({
      termMonths,
      years: termMonths / 12,
      label: billingTerms.domain.label?.[termMonths] || yearsLabel(termMonths / 12),
    }));
}

// One renewal per domain per cart: adding it again with other years replaces it.
export const renewalLineId = (domain) => `RENEW:${domain}`;

// The line intent squish-backend prices (POST /api/carts/quote).
export function renewalLine(domain, termMonths) {
  return {
    productCode: 'DOMAIN',
    productName: `Renew ${domain} (${yearsLabel(termMonths / 12)})`,
    quantity: 1,
    metadata: { domain, kind: 'renew', termMonths },
  };
}

// Why the rep can't add a renewal right now (null = addable).
//   domains: null while loading, { error } when the account's products failed,
//            otherwise the array from renewableDomains.
export function renewalBlocker({ isInvoice, isProspect, terms, domains }) {
  if (isInvoice) return 'Card checkout only: switch to Card to sell a renewal';
  if (isProspect) return 'A renewal is for a domain they already have, so it needs the customer\'s account';
  if (!terms?.length) return 'Domain terms unavailable';
  if (domains == null) return 'Loading this account\'s domains…';
  if (domains.error) return `Couldn't load this account's domains: ${domains.error}`;
  if (domains.length === 0) return 'No domain on this account to renew.';
  return null;
}
